import Cookies from "js-cookie";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "../interfaces/user";
import type { UserContextType } from "../interfaces/storeInterfaces/UserContextType";

function getInitialUser(): User | null {
  const savedUser = Cookies.get("tutorInfo");

  if (!savedUser) {
    console.log("No tutorInfo cookie found");
    return null;
  }

  try {
    const user = JSON.parse(decodeURIComponent(savedUser));

    return user;
  } catch (error) {
    console.error("Error parsing cookie:", error);
    Cookies.remove("tutorInfo");
    return null;
  }
}



const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(getInitialUser);

  const setUser = useCallback((userData: User | null) => {
    setUserState(userData);
    if (userData) {
      Cookies.set("tutorInfo", encodeURIComponent(JSON.stringify(userData)), {
        secure: true,
        expires: 365,
      });
    } else {
      Cookies.remove("tutorInfo");
    }
  }, []);

  const login = useCallback(
    (userData: User) => {
      if (userData) {
        setUser(userData);
      }
    },
    [setUser]
  );

  const logout = useCallback(() => {
    setUser(null);
  }, [setUser]);

  // Memoize the context value to ensure proper re-renders when user state changes
  const value: UserContextType = useMemo(
    () => ({
      user,
      setUser,
      isAuthenticated: user !== null,
      login,
      logout,
    }),
    [user, setUser, login, logout]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
